import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../../api/client.js';
import type { BotSettings, BotTimer, BotVariable } from '../../api/types.js';
import { Alert } from '../../components/Alert.js';
import { EmptyState } from '../../components/EmptyState.js';
import { PlatformIcon } from '../../components/PlatformIcon.js';
import { StatusPill } from '../../components/StatusPill.js';
import { Tooltip } from '../../components/Tooltip.js';
import { useSession } from '../../auth/session-context.js';
import { VariableHelp } from './BotResponseFields.js';

/**
 * Scheduled messages (§102, module brief §40).
 *
 * ==================== TWO LIMITATIONS SAID AT CONFIGURATION TIME ====================
 * A timer's two optional conditions both have honest caveats, and both are
 * stated on the form rather than discovered by watching nothing happen:
 *
 *   - The minimum interval is five minutes, because providers treat
 *     unsolicited repetition as spam and the floor is a guardrail against a
 *     customer configuring their own account into trouble.
 *   - "Only while live" is honoured only where Connaro can tell. On platforms
 *     it cannot ask, the timer FIRES — a silently-never-posting timer is worse
 *     than one that posts off air, and the operator is told which behaviour
 *     they are getting before they rely on it.
 * ===================================================================================
 */
export function BotTimers({
  settings,
  variables,
}: {
  settings: BotSettings;
  variables: BotVariable[];
}): React.JSX.Element {
  const { can } = useSession();
  const mayManage = can('bot.timers.manage');

  const [timers, setTimers] = useState<BotTimer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{
    id: string | null;
    name: string;
    intervalMinutes: number;
    minMessages: string;
    liveOnly: boolean;
    messages: string[];
    targetPlatforms: string[];
    enabled: boolean;
  } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await apiRequest<{ timers: BotTimer[] }>('/bot/timers');
      setTimers(data.timers);
    } catch (caught) {
      setTimers([]);
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not read the timers.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (): Promise<void> => {
    if (draft === null) return;
    setBusy(true);
    setError(null);

    try {
      await apiRequest(draft.id === null ? '/bot/timers' : `/bot/timers/${draft.id}`, {
        method: draft.id === null ? 'POST' : 'PUT',
        body: {
          name: draft.name,
          enabled: draft.enabled,
          intervalSeconds: draft.intervalMinutes * 60,
          minMessages: draft.minMessages === '' ? null : Number(draft.minMessages),
          liveOnly: draft.liveOnly,
          targetMode: 'selected',
          targetPlatforms: draft.targetPlatforms,
          messages: draft.messages.filter((message) => message.trim() !== ''),
        },
      });
      setDraft(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That timer could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bot__section">
      {error !== null && <Alert tone="error">{error}</Alert>}

      {mayManage && draft === null && (
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setDraft({
              id: null,
              name: '',
              intervalMinutes: 15,
              minMessages: '',
              liveOnly: false,
              messages: [''],
              targetPlatforms: settings.platforms.map((entry) => entry.platform),
              enabled: false,
            });
          }}
        >
          New timer
        </button>
      )}

      {draft !== null && (
        <form
          className="bot__form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="composer__field">
            <label className="field__label" htmlFor="timer-name">
              Name
            </label>
            <input
              id="timer-name"
              className="field__input"
              value={draft.name}
              onChange={(event) => {
                setDraft({ ...draft, name: event.target.value });
              }}
            />
          </div>

          <div className="bot__row">
            <div className="composer__field">
              <label className="field__label" htmlFor="timer-interval">
                Every (minutes)
              </label>
              <input
                id="timer-interval"
                className="field__input"
                type="number"
                min={5}
                value={draft.intervalMinutes}
                onChange={(event) => {
                  setDraft({ ...draft, intervalMinutes: Number(event.target.value) });
                }}
              />
              <span className="field__hint">
                Five minutes is the shortest allowed. Platforms treat frequent unsolicited posting
                as spam.
              </span>
            </div>

            <div className="composer__field">
              <label className="field__label" htmlFor="timer-activity">
                Only if chat has seen at least
              </label>
              <input
                id="timer-activity"
                className="field__input"
                type="number"
                min={0}
                placeholder="no minimum"
                value={draft.minMessages}
                onChange={(event) => {
                  setDraft({ ...draft, minMessages: event.target.value });
                }}
              />
              <span className="field__hint">messages since it last posted. Leave empty for none.</span>
            </div>
          </div>

          <label className="bot__switch">
            <input
              type="checkbox"
              checked={draft.liveOnly}
              onChange={(event) => {
                setDraft({ ...draft, liveOnly: event.target.checked });
              }}
            />
            <span>Only while live</span>
          </label>
          {/*
           * The honest caveat, at the moment the operator ticks the box. §24
           * forbids faking live detection, so the behaviour on a platform
           * Connaro cannot ask has to be stated rather than assumed.
           */}
          <span className="field__hint">
            Connaro can only honour this where the platform tells it whether you are broadcasting.
            Where it cannot tell, the timer still posts — a timer that silently never ran would be
            harder to notice than one that posted off air.
          </span>

          {draft.messages.map((message, index) => (
            <div className="composer__field" key={index}>
              <label className="field__label" htmlFor={`timer-message-${String(index)}`}>
                Message {String(index + 1)}
              </label>
              <textarea
                id={`timer-message-${String(index)}`}
                className="field__input"
                rows={2}
                value={message}
                onChange={(event) => {
                  const next = [...draft.messages];
                  next[index] = event.target.value;
                  setDraft({ ...draft, messages: next });
                }}
              />
            </div>
          ))}

          <div className="bot__actions">
            <button
              type="button"
              className="btn"
              onClick={() => {
                setDraft({ ...draft, messages: [...draft.messages, ''] });
              }}
            >
              Add another message
            </button>
          </div>

          <label className="bot__switch">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => {
                setDraft({ ...draft, enabled: event.target.checked });
              }}
            />
            <span>Enabled</span>
          </label>

          <div className="bot__actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save timer'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setDraft(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {timers === null ? (
        <p className="field__hint">Loading…</p>
      ) : timers.length === 0 ? (
        <EmptyState title="No timers yet">A timer posts a message on a schedule — a reminder about your socials, or your schedule.</EmptyState>
      ) : (
        <ul className="bot__list">
          {timers.map((timer) => (
            <li className="bot__item" key={timer.id}>
              <div className="bot__item-head">
                <span className="bot__item-name">{timer.name}</span>
                <StatusPill
                  tone={timer.enabled ? 'ok' : 'idle'}
                  label={timer.enabled ? 'Enabled' : 'Disabled'}
                />
                {timer.targetPlatforms.map((platform) => (
                  <PlatformIcon
                    key={platform}
                    platform={platform}
                    displayName={platform}
                    size={16}
                    label={platform}
                  />
                ))}
              </div>
              <span className="field__hint">
                Every {String(Math.round(timer.intervalSeconds / 60))} minutes ·{' '}
                {String(timer.messages.length)} message{timer.messages.length === 1 ? '' : 's'}
                {timer.minMessages === null ? '' : ' · '}
                {timer.minMessages !== null && (
                  <Tooltip tip="The timer waits until chat has been this active since it last posted, so it does not talk to an empty room.">
                    <span>after {String(timer.minMessages)} chat messages</span>
                  </Tooltip>
                )}
                {timer.liveOnly ? ' · ' : ''}
                {timer.liveOnly && (
                  <Tooltip tip="Honoured only where the platform tells Connaro whether you are broadcasting. Where it cannot tell, the timer still posts.">
                    <span>only while live</span>
                  </Tooltip>
                )}
              </span>
              <span className="field__hint">
                {timer.lastExecutedAt === null
                  ? 'Has not run yet'
                  : `Last ran ${new Date(timer.lastExecutedAt).toLocaleString()}`}
              </span>

              {mayManage && (
                <div className="bot__actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setDraft({
                        id: timer.id,
                        name: timer.name,
                        intervalMinutes: Math.round(timer.intervalSeconds / 60),
                        minMessages: timer.minMessages === null ? '' : String(timer.minMessages),
                        liveOnly: timer.liveOnly,
                        messages: timer.messages,
                        targetPlatforms: timer.targetPlatforms,
                        enabled: timer.enabled,
                      });
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <VariableHelp variables={variables} />
    </div>
  );
}
